/**
 * Currency registry — the single source of truth for which currencies the V5 paywall
 * supports, their USD multiplier, decimal convention, selection mode, and the countries
 * that map to them.
 *
 * The raw data lives in `currencies.data.json` so it can be shared verbatim with the
 * Node price tooling (`scripts/create-stripe-prices.js`) — there is exactly ONE place
 * that defines the multipliers/country lists, so the displayed price and the Stripe
 * Price object can never drift.
 *
 * Pricing model (decision: "your list as-is"):
 *   - EUR / GBP / CHF : ×1.0  → same number as USD ($4.99 → £4.99)
 *   - AUD / CAD / NZD : ×1.5  → a little higher (those dollars are "cheaper")
 *   - VUV             : ×15, zero-decimal, mode 'test' — a cheap real-money test currency
 *                       (Vanuatu Vatu, ~120 VUV ≈ $1). Reachable ONLY via the md5 URL
 *                       unlock, never via geo, so a real Vanuatu visitor falls back to USD.
 */

import currenciesData from './currencies.data.json';

export type CurrencyMode = 'geo' | 'test';

export interface CurrencyDef {
  multiplier: number;
  zeroDecimal: boolean;
  mode: CurrencyMode;
  countries: string[];
}

// Typed view over the shared JSON (mode comes through as a plain string in JSON).
export const CURRENCIES = currenciesData as unknown as Record<string, CurrencyDef>;

export type CurrencyCode = keyof typeof currenciesData & string;

/** Fallback / default currency used whenever geo doesn't resolve to a supported one. */
export const DEFAULT_CURRENCY: CurrencyCode = 'USD';

/** Every code the app understands (USD, EUR, GBP, CHF, AUD, CAD, NZD, VUV). */
export const SUPPORTED_CURRENCIES: string[] = Object.keys(CURRENCIES);

export function getCurrencyDef(code: string): CurrencyDef | null {
  return (code && CURRENCIES[code.toUpperCase()]) || null;
}

export function isSupportedCurrency(code: string): boolean {
  return getCurrencyDef(code) !== null;
}

/**
 * Convert a base USD price into the local "beautiful" charm price for a currency.
 *   - zero-decimal (VUV): round(usd × multiplier)            → whole number (180, 600, 1200)
 *   - 2-decimal:          round(usd × multiplier) − 0.01     → always ends in .99; a no-op at ×1.0
 *     (round(39.99) − 0.01 = 39.99; round(39.99 × 1.5) − 0.01 = round(59.985) − 0.01 = 59.99)
 *
 * MUST stay in sync with `beautifyPrice()` in scripts/create-stripe-prices.js
 * (enforced by scripts/test-currency-parity.test.js).
 */
export function beautifyPrice(usdPrice: number, code: string): number {
  const def = getCurrencyDef(code);
  if (!def) return usdPrice;
  const scaled = usdPrice * def.multiplier;
  return def.zeroDecimal ? Math.round(scaled) : Math.round(scaled) - 0.01;
}

/**
 * Number of decimal places for a currency (0 for zero-decimal currencies like VUV, else 2).
 */
export function decimalsForCurrency(code: string): number {
  return getCurrencyDef(code)?.zeroDecimal ? 0 : 2;
}

/**
 * Smallest currency unit (cents / whole vatu) for a major-unit amount — used for Stripe
 * `unit_amount` / `currency_options` and express-checkout amounts.
 */
export function toMinorUnits(amount: number, code: string): number {
  return decimalsForCurrency(code) === 0 ? Math.round(amount) : Math.round(amount * 100);
}

/**
 * Map a 2-letter ISO country code to a geo currency. Returns DEFAULT_CURRENCY (USD) for
 * unknown countries and NEVER returns a `mode: 'test'` currency (e.g. VUV).
 */
export function currencyForCountry(country: string | undefined | null): CurrencyCode {
  if (!country) return DEFAULT_CURRENCY;
  const cc = country.toUpperCase();
  for (const code of Object.keys(CURRENCIES)) {
    const def = CURRENCIES[code];
    if (def.mode === 'geo' && def.countries.includes(cc)) {
      return code as CurrencyCode;
    }
  }
  return DEFAULT_CURRENCY;
}
